// Idempotent producer for a business's pending change set on this device.
//
// The sync engine asks this producer to make sure there is an outbox row ready
// to push. The answer is always the same for the same business: the producer
// checks the durable outbox first and only ever writes a row when there is not
// one already. That single rule is what keeps two devices from exchanging
// duplicate versions of the same change, and it is why a resumed run cannot
// re-enqueue something that was already pushed.
//
// The entry carries the business row's payload and version — without a payload
// the change fails `validateChange` (`UPSERT_WITHOUT_PAYLOAD`) and is silently
// filtered out by the snapshot path, leaving the cloud with no business row.

import type { DNRDatabase } from '@data/db/database';
import type { SyncOutboxEntry } from '@data/db/types';
import { newId } from '@platform/ids';
import { currentDeviceId } from './deviceIdentity';

export async function ensureBusinessChangeEnqueued(
  db: DNRDatabase,
  businessId: string,
): Promise<void> {
  // Look up an existing business change by business + entity type + entity id,
  // not by assuming changeId === businessId. The old lookup found nothing unless
  // a previous (broken) run had stored changeId === businessId.
  const existing = await db.syncOutbox
    .where('businessId')
    .equals(businessId)
    .filter((e) => e.entityType === 'business' && e.entityId === businessId)
    .first();

  if (existing) {
    // A valid entry already exists (has a changeId and a payload). Never
    // duplicate it — a resumed run must not re-enqueue something already pushed.
    if (existing.changeId && existing.payload) return;

    // Entry exists but is missing its payload (written by the old code path).
    // Repair it in place rather than creating a second entry. The change keeps
    // the row's current version — nextVersion would wrongly claim a transition
    // that never happened.
    const business = await db.businesses.get(businessId);
    if (business) {
      await db.syncOutbox.put({
        ...existing,
        payload: business as unknown as Record<string, unknown>,
        version: business.syncVersion ?? 1,
        changedAt: new Date().toISOString(),
      });
    }
    return;
  }

  const business = await db.businesses.get(businessId);
  if (!business) return; // business row gone; nothing to sync

  const now = new Date().toISOString();

  // A NEW CHANGE ID, ALWAYS — because this device cannot prove it minted the
  // old one.
  //
  // This branch is reached only when the outbox holds NO entry for the
  // business, and the outbox is the only durable record of the changes this
  // device authored. A `syncChangeId` sitting on the row without an entry
  // beside it therefore names a mutation that arrived FROM the cloud:
  // `applyChanges` stamps `syncChangeId: change.changeId` on every row it
  // applies, and `restoreCloudBusiness` applies a whole snapshot. Reusing that
  // id re-sends someone else's change under our device id, with our `changedAt`
  // and our payload — and `sync_push_changes` refuses exactly that ("A change id
  // was reused with different content"), permanently, because the rejection is
  // non-retryable. That is the first sync after every cloud restore.
  //
  // Reuse is still correct, and still happens, when the entry EXISTS: the early
  // return above leaves it untouched, which is what lets a pruned business be
  // republished under its original change id.
  //
  // The version stays the row's. This is a re-publication of the same state, and
  // claiming a newer version would let a restored device overwrite a peer's
  // genuinely newer business profile.
  const changeId = newId();
  const entry: SyncOutboxEntry = {
    changeId,
    businessId,
    deviceId: await currentDeviceId(db),
    entityType: 'business',
    entityId: businessId,
    operation: 'UPSERT',
    version: business.syncVersion ?? 1,
    status: 'pending',
    retryCount: 0,
    changedAt: now,
    createdAt: now,
    payload: business as unknown as Record<string, unknown>,
  };

  // The entry and the row's pointer to it are written together. A row naming a
  // change the outbox does not hold would fail `recoverBusinessEntity`'s lookup
  // (it derives the change id from the row), and an entry the row does not name
  // would be re-minted as a second, unrelated change on the next push.
  await db.transaction('rw', db.businesses, db.syncOutbox, async () => {
    await db.syncOutbox.put(entry);
    await db.businesses.update(businessId, { syncChangeId: changeId });
  });
}
